import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, IconButton, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, InputLabel, InputAdornment,
  CircularProgress, Card, CardContent, Stack, Tooltip, ImageList,
  ImageListItem, Divider, Grid
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  createRevenue, getMyRevenues, getRevenueBySession,
  updateRevenue, deleteEvidence, getRevenueAuditLog
} from '../services/revenueService';
import { extractGMVFromImage } from '../services/ocrService';
import { getLivestreamHistory } from '../services/livestreamService';
import { getMyCommissionConfig } from '../services/commissionConfigService';

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '0 ₫';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(amount);
};

const toDisplayableImageUrl = (url) => {
  if (!url) return url;
  if (/\.(heic|heif)$/i.test(url)) {
    return url.replace('/upload/', '/upload/f_jpg/');
  }
  return url;
};

const formatVND = (value) => {
  if (!value && value !== 0) return '';
  const num = String(value).replace(/\D/g, '');
  if (!num) return '';
  return num.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseVND = (formatted) => {
  if (!formatted) return '';
  return formatted.replace(/\./g, '');
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('vi-VN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const statusConfig = {
  draft: { label: 'Nháp', color: 'default', icon: <EditIcon fontSize="small" /> },
  submitted: { label: 'Chờ duyệt', color: 'warning', icon: <HourglassEmptyIcon fontSize="small" /> },
  approved: { label: 'Đã duyệt', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  rejected: { label: 'Từ chối', color: 'error', icon: <CancelIcon fontSize="small" /> },
};

const RevenueSubmit = ({ userId, isMobile = false }) => {
  const [livestreams, setLivestreams] = useState([]);
  const [revenues, setRevenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, data: null });
  const [auditDialog, setAuditDialog] = useState({ open: false, logs: [] });
  const [imagePreview, setImagePreview] = useState({ open: false, url: '' });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const [form, setForm] = useState({
    livestreamId: '',
    totalSales: '',
    platform: 'other',
    sessionLink: '',
    sessionCode: '',
    notes: '',
    sessionStartTime: '',
    sessionEndTime: '',
  });
  const [files, setFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [commissionConfig, setCommissionConfig] = useState(null);
  const [showCustomTime, setShowCustomTime] = useState(false);
  const [showGuide, setShowGuide] = useState(() => {
    const saved = localStorage.getItem('revenueGuideVisible');
    return saved === null ? true : saved === 'true';
  });
  const [ocrResults, setOcrResults] = useState([]);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrFailed, setOcrFailed] = useState(false);
  const [gmvOverride, setGmvOverride] = useState(false); // user yêu cầu sửa GMV
  const [gmvOverrideReason, setGmvOverrideReason] = useState('');

  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const fetchData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [revenueResult, historyResult, configResult] = await Promise.all([
        getMyRevenues({ limit: 100 }),
        getLivestreamHistory(userId, { limit: 100, sortBy: 'date', sortOrder: 'desc' }),
        getMyCommissionConfig()
      ]);

      if (revenueResult.success) {
        setRevenues(revenueResult.data || []);
      }
      if (historyResult.success) {
        setLivestreams(historyResult.data?.records || historyResult.data || []);
      }
      if (configResult.success && configResult.data?.isActive) {
        setCommissionConfig(configResult.data);
      }
    } catch (err) {
      console.error('Fetch data error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Trạng thái từng phiên cho picker khai báo doanh thu:
  //  - declared: đã có doanh thu → vẫn hiện nhưng khoá + nhãn "đã khai báo"
  //  - phiên CHƯA khai & quá 3 ngày → ẩn khỏi picker (hết hạn khai)
  const DECLARE_WINDOW_DAYS = 3;
  const pickerLivestreams = livestreams
    .map(ls => {
      const lsId = ls._id?.toString();
      const declared = revenues.some(r => (r.livestreamId?._id || r.livestreamId || '').toString() === lsId);
      const daysSince = ls.date ? Math.floor((Date.now() - new Date(ls.date).getTime()) / 86400000) : 0;
      return { ...ls, declared, daysSince };
    })
    .filter(ls => ls.declared || ls.daysSince <= DECLARE_WINDOW_DAYS);
  // Phiên còn CHỌN được để khai (chưa khai + trong hạn 3 ngày)
  const availableLivestreams = pickerLivestreams.filter(ls => !ls.declared);

  const handleOpenCreate = () => {
    setForm({ livestreamId: '', totalSales: '', platform: 'other', sessionLink: '', sessionCode: '', notes: '', sessionStartTime: '', sessionEndTime: '' });
    setFiles([]);
    setOcrResults([]);
    setOcrLoading(false);
    setGmvOverride(false);
    setGmvOverrideReason('');
    setEditingId(null);
    setShowCustomTime(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (revenue) => {
    setForm({
      livestreamId: revenue.livestreamId?._id || revenue.livestreamId,
      totalSales: revenue.totalSales.toString(),
      platform: revenue.platform || 'other',
      sessionLink: revenue.sessionLink || '',
      sessionCode: revenue.sessionCode || '',
      notes: revenue.notes || '',
      sessionStartTime: revenue.sessionStartTime || '',
      sessionEndTime: revenue.sessionEndTime || '',
    });
    setFiles([]);
    setEditingId(revenue._id);
    setDialogOpen(true);
  };

  const handleFileChange = async (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length + files.length > 5) {
      showNotification('Tối đa 5 ảnh', 'warning');
      return;
    }
    const startIdx = files.length;
    setFiles(prev => [...prev, ...selected]);

    // Chạy OCR cho ảnh đầu tiên (thường là ảnh chính chứa GMV)
    if (selected.length > 0 && startIdx === 0) {
      setOcrLoading(true);
      setOcrFailed(false);

      // Auto-stop sau 30s
      const ocrTimeout = setTimeout(() => {
        setOcrLoading(false);
        setOcrFailed(true);
        showNotification('AI đọc ảnh quá lâu. Bạn có thể nhập doanh thu bằng tay.', 'warning');
      }, 30000);

      try {
        const result = await extractGMVFromImage(selected[0]);
        clearTimeout(ocrTimeout);
        if (result.success && result.data) {
          const ocr = result.data;
          setOcrResults(prev => {
            const newResults = [...prev];
            newResults[0] = ocr;
            return newResults;
          });
          if (!form.totalSales && ocr.gmv > 0 && ocr.confidence >= 0.7) {
            setForm(prev => ({ ...prev, totalSales: formatVND(ocr.gmv) }));
            showNotification(`AI đọc được doanh thu: ${formatMoney(ocr.gmv)}`, 'info');
          } else if (ocr.gmv > 0 && ocr.confidence < 0.7) {
            showNotification('AI đọc được nhưng độ tin cậy thấp. Vui lòng kiểm tra lại.', 'warning');
          }
          if (form.platform === 'other' && ocr.platform && ocr.platform !== 'unknown') {
            setForm(prev => ({ ...prev, platform: ocr.platform }));
          }
        } else {
          setOcrFailed(true);
          showNotification('Không đọc được ảnh. Vui lòng nhập doanh thu bằng tay.', 'warning');
        }
      } catch (err) {
        clearTimeout(ocrTimeout);
        console.error('OCR error:', err);
        setOcrFailed(true);
        showNotification('Lỗi đọc ảnh. Vui lòng nhập doanh thu bằng tay.', 'warning');
      } finally {
        clearTimeout(ocrTimeout);
        setOcrLoading(false);
      }
    }
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setOcrResults(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!editingId && !form.livestreamId) {
      showNotification('Vui lòng chọn phiên live', 'error');
      return;
    }
    const salesNumber = parseInt(parseVND(String(form.totalSales)), 10);
    if (!salesNumber || salesNumber <= 0) {
      showNotification('Vui lòng nhập doanh thu hợp lệ (lớn hơn 0)', 'error');
      return;
    }
    if (!editingId && files.length === 0) {
      showNotification('Phải upload ít nhất 1 ảnh chụp màn hình', 'error');
      return;
    }
    // Validate lý do sửa nếu user override GMV
    if (gmvOverride && !gmvOverrideReason.trim()) {
      showNotification('Vui lòng nhập lý do sửa doanh thu', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      if (!editingId) formData.append('livestreamId', form.livestreamId);
      formData.append('totalSales', salesNumber);
      formData.append('platform', form.platform);
      formData.append('sessionLink', form.sessionLink);
      formData.append('sessionCode', form.sessionCode);
      formData.append('notes', form.notes);
      if (form.sessionStartTime) formData.append('sessionStartTime', form.sessionStartTime);
      if (form.sessionEndTime) formData.append('sessionEndTime', form.sessionEndTime);
      files.forEach(f => formData.append('evidence', f));
      // Gửi kèm OCR data nếu có
      if (ocrResults[0] && ocrResults[0].gmv > 0) {
        const ocr = ocrResults[0];
        formData.append('ocrExtractedGMV', ocr.gmv);
        formData.append('ocrConfidence', ocr.confidence);
        formData.append('ocrRawText', ocr.rawText || '');
        if (ocr.platform) formData.append('ocrPlatform', ocr.platform);
        if (ocr.duration) formData.append('ocrDuration', ocr.duration);
        if (ocr.orders) formData.append('ocrOrders', ocr.orders);
        if (ocr.date) formData.append('ocrDate', ocr.date);
        if (ocr.viewers) formData.append('ocrViewers', ocr.viewers);
        if (gmvOverride) {
          formData.append('ocrOverridden', 'true');
          formData.append('ocrOverrideReason', gmvOverrideReason.trim());
        }
      }

      const result = editingId
        ? await updateRevenue(editingId, formData)
        : await createRevenue(formData);

      if (result.success) {
        showNotification(editingId ? 'Cập nhật thành công' : 'Khai báo doanh thu thành công');
        setDialogOpen(false);
        fetchData();
      } else {
        showNotification(result.error?.message || 'Có lỗi xảy ra', 'error');
      }
    } catch (err) {
      showNotification('Có lỗi xảy ra', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewAudit = async (revenueId) => {
    try {
      const result = await getRevenueAuditLog(revenueId);
      if (result.success) {
        setAuditDialog({ open: true, logs: result.data || [] });
      }
    } catch (err) {
      showNotification('Không thể tải lịch sử', 'error');
    }
  };

  const handleDeleteEvidence = async (revenueId, evidenceId) => {
    if (!window.confirm('Xóa ảnh này?')) return;
    try {
      const result = await deleteEvidence(revenueId, evidenceId);
      if (result.success) {
        showNotification('Đã xóa ảnh');
        fetchData();
      } else {
        showNotification(result.error?.message || 'Không thể xóa', 'error');
      }
    } catch (err) {
      showNotification('Có lỗi xảy ra', 'error');
    }
  };

  const selectedLivestream = livestreams.find(ls => ls._id === form.livestreamId);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MonetizationOnIcon color="primary" />
          Doanh thu livestream
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddPhotoAlternateIcon />}
          onClick={handleOpenCreate}
          disabled={availableLivestreams.length === 0}
          size={isMobile ? 'small' : 'medium'}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          Khai báo doanh thu
        </Button>
      </Box>

      {availableLivestreams.length === 0 && revenues.length === 0 && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Chưa có phiên live nào để khai báo doanh thu. Hãy chấm công trước.
        </Alert>
      )}

      {/* Revenue List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : revenues.length > 0 ? (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>Ngày</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Giờ live</TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Nền tảng</TableCell>}
                <TableCell sx={{ fontWeight: 700 }} align="right">Doanh thu</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Trạng thái</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {revenues.map((rev) => {
                const st = statusConfig[rev.status] || statusConfig.draft;
                const ls = rev.livestreamId;
                return (
                  <TableRow key={rev._id} hover>
                    <TableCell>{formatDate(rev.date)}</TableCell>
                    <TableCell>{ls?.hours ? `${ls.hours}h` : '-'}</TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Chip label={rev.platform === 'shopee' ? 'Shopee' : rev.platform === 'tiktok' ? 'TikTok' : 'Khác'} size="small" variant="outlined" />
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#059669' }}>
                      {formatMoney(rev.totalSales)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip icon={st.icon} label={st.label} color={st.color} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Chi tiết">
                          <IconButton size="small" onClick={() => setDetailDialog({ open: true, data: rev })}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {['draft', 'rejected'].includes(rev.status) && (
                          <Tooltip title="Sửa">
                            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(rev)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Lịch sử">
                          <IconButton size="small" onClick={() => handleViewAudit(rev._id)}>
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #059669 100%)',
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', gap: 1.5
        }}>
          <MonetizationOnIcon sx={{ color: 'white', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
              {editingId ? 'Sửa khai báo doanh thu' : 'Khai báo doanh thu'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Vui lòng điền đầy đủ thông tin bên dưới
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, py: 2.5 }}>
          {/* Hướng dẫn nhập */}
          <Box sx={{ mb: 2 }}>
            <Box
              onClick={() => {
                const next = !showGuide;
                setShowGuide(next);
                localStorage.setItem('revenueGuideVisible', String(next));
              }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                color: '#2563eb', mb: showGuide ? 1 : 0,
                '&:hover': { color: '#1d4ed8' },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                Hướng dẫn nhập khai báo
              </Typography>
              {showGuide ? <ExpandLessIcon sx={{ fontSize: 16, ml: 'auto' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, ml: 'auto' }} />}
            </Box>
            {showGuide && (
              <Alert severity="info" icon={false} sx={{
                borderRadius: 2, py: 1, px: 1.5,
                bgcolor: '#eff6ff', border: '1px solid #bfdbfe',
                '& .MuiAlert-message': { width: '100%' },
              }}>
                <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { fontSize: '0.72rem', color: '#334155', mb: 0.5, lineHeight: 1.5 } }}>
                  <li><b>Chọn phiên live</b> — Chọn phiên livestream muốn khai báo</li>
                  <li><b>Upload ảnh chụp</b> — Chụp màn hình doanh thu từ app (Shopee/TikTok), AI sẽ tự đọc số liệu</li>
                  <li><b>Kiểm tra doanh thu</b> — Xác nhận số VND đúng, nếu sai nhấn "Yêu cầu sửa"</li>
                  <li><b>Nền tảng & link</b> — Chọn nền tảng và dán link phiên live</li>
                  <li><b>Khung giờ</b> — Chọn khung giờ để tính hoa hồng</li>
                  <li style={{ marginBottom: 0 }}><b>Gửi duyệt</b> — Kiểm tra lại thông tin rồi nhấn "Gửi duyệt"</li>
                </Box>
              </Alert>
            )}
          </Box>

          {/* Chọn phiên live */}
          {!editingId ? (
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#334155' }}>
                Phiên livestream *
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={form.livestreamId}
                  onChange={(e) => setForm({ ...form, livestreamId: e.target.value })}
                  displayEmpty
                  sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}
                >
                  <MenuItem value="" disabled>
                    <Typography color="text.secondary">-- Chọn phiên live --</Typography>
                  </MenuItem>
                  {pickerLivestreams.length === 0 && (
                    <MenuItem value="" disabled>
                      <Typography color="text.secondary" variant="body2">Không có phiên nào để khai báo</Typography>
                    </MenuItem>
                  )}
                  {pickerLivestreams.map(ls => (
                    <MenuItem key={ls._id} value={ls._id} disabled={ls.declared}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                        <Chip label={formatDate(ls.date)} size="small" sx={{ fontWeight: 600, bgcolor: '#e0f2fe', color: '#0369a1' }} />
                        <Typography variant="body2" fontWeight={600}>{ls.hours}h livestream</Typography>
                        {ls.declared && (
                          <Chip label="✓ đã khai báo" size="small" color="success" variant="outlined" sx={{ ml: 'auto', fontSize: '0.7rem' }} />
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : (
            <Alert icon={false} sx={{ mb: 2.5, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={formatDate(revenues.find(r => r._id === editingId)?.date)} size="small" sx={{ fontWeight: 600, bgcolor: '#2563eb', color: 'white' }} />
                <Typography variant="body2" fontWeight={600}>Đang sửa khai báo</Typography>
              </Stack>
            </Alert>
          )}

          {/* Doanh thu - VND format */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#334155' }}>
              Doanh thu (VND) *
            </Typography>
            {/* Input GMV - disabled cho đến khi AI đọc, fail, hoặc user yêu cầu sửa */}
            <TextField
              fullWidth
              value={formatVND(form.totalSales)}
              onChange={(e) => {
                if (editingId || gmvOverride || ocrFailed || !form.totalSales) {
                  setForm({ ...form, totalSales: parseVND(e.target.value) });
                }
              }}
              disabled={!editingId && !gmvOverride && !ocrFailed && !form.totalSales && !ocrLoading}
              placeholder={ocrFailed ? "Nhập doanh thu bằng tay..." : (!editingId && files.length === 0 ? "Upload ảnh để AI tự đọc doanh thu" : "Đang chờ AI đọc...")}
              size="small"
              InputProps={{
                readOnly: !editingId && !gmvOverride && !ocrFailed && !!form.totalSales,
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 700, color: '#059669', fontSize: '1.1rem' }}>₫</Typography>
                  </InputAdornment>
                ),
                endAdornment: form.totalSales ? (
                  <InputAdornment position="end">
                    <Typography variant="caption" color="text.secondary">VNĐ</Typography>
                  </InputAdornment>
                ) : null,
                sx: { fontWeight: 600, fontSize: '1.1rem', letterSpacing: '0.5px' }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: (!editingId && !gmvOverride && !form.totalSales) ? '#f1f5f9' : gmvOverride ? '#fffbeb' : '#f0fdf4',
                  '&.Mui-focused': { bgcolor: '#ffffff' }
                }
              }}
            />
            {form.totalSales && parseInt(form.totalSales) > 0 && (
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#059669', fontWeight: 500 }}>
                = {formatMoney(parseInt(form.totalSales))}
              </Typography>
            )}
            {ocrFailed && !ocrLoading && (
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#d97706', fontWeight: 600 }}>
                AI không đọc được — vui lòng nhập doanh thu bằng tay
              </Typography>
            )}

            {/* Nút yêu cầu sửa GMV - chỉ hiện khi AI đã đọc và user chưa bấm sửa */}
            {!editingId && ocrResults[0]?.gmv > 0 && !gmvOverride && (
              <Button
                size="small"
                onClick={() => setGmvOverride(true)}
                sx={{
                  mt: 0.5, textTransform: 'none', fontSize: '0.75rem',
                  color: '#d97706', fontWeight: 600,
                  '&:hover': { bgcolor: '#fffbeb' }
                }}
              >
                AI đọc sai? Yêu cầu sửa
              </Button>
            )}

            {/* Lý do sửa - bắt buộc khi user override */}
            {gmvOverride && (
              <TextField
                fullWidth
                size="small"
                value={gmvOverrideReason}
                onChange={(e) => setGmvOverrideReason(e.target.value)}
                placeholder="Nhập lý do sửa doanh thu (bắt buộc)..."
                required
                sx={{
                  mt: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2, bgcolor: '#fffbeb',
                    border: '1px solid #fde68a',
                  }
                }}
                helperText={`AI đọc: ${formatMoney(ocrResults[0]?.gmv || 0)} → Bạn sửa: ${form.totalSales ? formatMoney(parseInt(parseVND(String(form.totalSales)))) : '...'}`}
              />
            )}
          </Box>

          {/* Nền tảng + Link */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#334155' }}>
                Nền tảng
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={form.platform}
                  onChange={(e) => setForm({ ...form, platform: e.target.value })}
                  sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}
                >
                  <MenuItem value="shopee">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ee4d2d' }} />
                      <span>Shopee</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="tiktok">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#000000' }} />
                      <span>TikTok</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="other">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#94a3b8' }} />
                      <span>Khác</span>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#334155' }}>
                Link phiên live
              </Typography>
              <TextField
                fullWidth size="small" value={form.sessionLink}
                onChange={(e) => setForm({ ...form, sessionLink: e.target.value })}
                placeholder="https://shopee.vn/live/..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              />
            </Grid>
          </Grid>

          {/* Khung giờ phiên live */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: '#d97706' }} />
              Khung giờ phiên live
              <Typography component="span" sx={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.75rem', ml: 0.5 }}>
                (dùng tính hoa hồng)
              </Typography>
            </Typography>

            {/* Quick-select chips from commission config */}
            {commissionConfig && commissionConfig.timeSlots?.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
                {commissionConfig.timeSlots.map((slot, idx) => {
                  const isSelected = form.sessionStartTime === slot.startTime && form.sessionEndTime === slot.endTime;
                  return (
                    <Chip
                      key={idx}
                      icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                      label={
                        <span>
                          <strong>{slot.name}</strong>
                          <span style={{ opacity: 0.7, marginLeft: 4 }}>{slot.startTime}–{slot.endTime}</span>
                          <span style={{
                            marginLeft: 6,
                            background: isSelected ? 'rgba(255,255,255,0.3)' : '#fef3c7',
                            borderRadius: 8,
                            padding: '1px 6px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: isSelected ? '#fff' : '#92400e'
                          }}>{slot.rate}%</span>
                        </span>
                      }
                      onClick={() => {
                        if (isSelected) {
                          setForm(prev => ({ ...prev, sessionStartTime: '', sessionEndTime: '' }));
                        } else {
                          setForm(prev => ({ ...prev, sessionStartTime: slot.startTime, sessionEndTime: slot.endTime }));
                          setShowCustomTime(false);
                        }
                      }}
                      sx={{
                        height: 36,
                        borderRadius: '18px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        border: isSelected ? '2px solid #d97706' : '1.5px solid #e2e8f0',
                        bgcolor: isSelected ? '#d97706' : '#fff',
                        color: isSelected ? '#fff' : '#334155',
                        '& .MuiChip-icon': { color: isSelected ? '#fff' : '#d97706' },
                        '&:hover': {
                          bgcolor: isSelected ? '#b45309' : '#fffbeb',
                          borderColor: '#d97706',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(217,119,6,0.15)',
                        },
                        fontWeight: 500,
                        fontSize: '0.8rem',
                      }}
                    />
                  );
                })}

                {/* Nhập tay button */}
                <Chip
                  icon={<EditIcon sx={{ fontSize: 14 }} />}
                  label="Nhập giờ khác"
                  onClick={() => setShowCustomTime(prev => !prev)}
                  variant={showCustomTime ? 'filled' : 'outlined'}
                  sx={{
                    height: 36,
                    borderRadius: '18px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border: showCustomTime ? '2px solid #6366f1' : '1.5px dashed #cbd5e1',
                    bgcolor: showCustomTime ? '#6366f1' : 'transparent',
                    color: showCustomTime ? '#fff' : '#64748b',
                    '& .MuiChip-icon': { color: showCustomTime ? '#fff' : '#6366f1' },
                    '&:hover': {
                      bgcolor: showCustomTime ? '#4f46e5' : '#f1f5f9',
                      borderColor: '#6366f1',
                    },
                    fontWeight: 500,
                    fontSize: '0.8rem',
                  }}
                />
              </Stack>
            )}

            {/* Hiện trạng đã chọn */}
            {form.sessionStartTime && form.sessionEndTime && !showCustomTime && (
              <Paper elevation={0} sx={{
                p: 1, borderRadius: 2, mb: 1,
                bgcolor: '#fffbeb', border: '1px solid #fde68a',
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: '#d97706' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#92400e' }}>
                  {form.sessionStartTime} – {form.sessionEndTime}
                </Typography>
                <IconButton size="small" onClick={() => setForm(prev => ({ ...prev, sessionStartTime: '', sessionEndTime: '' }))} sx={{ ml: 'auto', p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                </IconButton>
              </Paper>
            )}

            {/* Custom time inputs - hiện khi không có config hoặc user chọn "Nhập giờ khác" */}
            {(!commissionConfig || !commissionConfig.timeSlots?.length || showCustomTime) && (
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" type="time" label="Bắt đầu"
                    value={form.sessionStartTime}
                    onChange={(e) => setForm({ ...form, sessionStartTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" type="time" label="Kết thúc"
                    value={form.sessionEndTime}
                    onChange={(e) => setForm({ ...form, sessionEndTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
                  />
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Mã phiên + Ghi chú */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={5}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#334155' }}>
                Mã phiên live
              </Typography>
              <TextField
                fullWidth size="small" value={form.sessionCode}
                onChange={(e) => setForm({ ...form, sessionCode: e.target.value })}
                placeholder="Nhập mã phiên..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#334155' }}>
                Ghi chú
              </Typography>
              <TextField
                fullWidth size="small" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Ghi chú thêm..."
                multiline rows={2}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              />
            </Grid>
          </Grid>

          {/* Ảnh bằng chứng */}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ mb: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                Ảnh chụp màn hình *
              </Typography>
              <Chip label={`${files.length}/5`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Upload ảnh chụp doanh thu từ app/web để xác minh. Bắt buộc ít nhất 1 ảnh.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            {files.map((file, idx) => (
              <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  position: 'relative', width: 88, height: 88, borderRadius: 2, overflow: 'hidden',
                  border: ocrResults[idx]?.confidence >= 0.7 ? '2px solid #22c55e' : '2px solid #e2e8f0',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#2563eb', transform: 'scale(1.03)' }
                }}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`preview-${idx}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  {/* OCR loading indicator */}
                  {ocrLoading && idx === 0 && !ocrResults[0] && (
                    <Box sx={{
                      position: 'absolute', inset: 0,
                      bgcolor: 'rgba(0,0,0,0.5)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                    </Box>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(idx)}
                    sx={{
                      position: 'absolute', top: 2, right: 2,
                      bgcolor: 'rgba(0,0,0,0.6)', color: 'white', p: 0.3,
                      '&:hover': { bgcolor: '#dc2626' }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                {/* OCR result badge */}
                {ocrResults[idx] && ocrResults[idx].gmv > 0 && (
                  <Tooltip title={
                    `${ocrResults[idx].platform !== 'unknown' ? ocrResults[idx].platform.toUpperCase() + ' | ' : ''}` +
                    `${ocrResults[idx].duration ? ocrResults[idx].duration + ' phút | ' : ''}` +
                    `${ocrResults[idx].orders ? ocrResults[idx].orders + ' đơn | ' : ''}` +
                    `${ocrResults[idx].viewers ? ocrResults[idx].viewers + ' views' : ''}`
                  } arrow>
                    <Chip
                      label={`AI: ${formatMoney(ocrResults[idx].gmv)}`}
                      size="small"
                      sx={{
                        height: 18, fontSize: '0.58rem', fontWeight: 700, maxWidth: 100,
                        bgcolor: ocrResults[idx].confidence >= 0.7 ? '#dcfce7' : '#fef3c7',
                        color: ocrResults[idx].confidence >= 0.7 ? '#15803d' : '#a16207',
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            ))}

            {files.length < 5 && (
              <Box
                component="label"
                sx={{
                  width: 88, height: 88, borderRadius: 2, cursor: 'pointer',
                  border: '2px dashed #cbd5e1', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 0.3,
                  transition: 'all 0.2s', bgcolor: '#f8fafc',
                  '&:hover': { borderColor: '#2563eb', bgcolor: '#eff6ff' }
                }}
              >
                <AddPhotoAlternateIcon sx={{ fontSize: 24, color: '#94a3b8' }} />
                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>Thêm ảnh</Typography>
                <input type="file" hidden accept="image/*" multiple onChange={handleFileChange} />
              </Box>
            )}
          </Box>

          {/* OCR Warnings - Cảnh báo khi dữ liệu user nhập không khớp AI đọc */}
          {ocrResults[0] && ocrResults[0].gmv > 0 && (() => {
            const ocr = ocrResults[0];
            const userGMV = parseInt(parseVND(String(form.totalSales)), 10) || 0;
            const warnings = [];
            // Cảnh báo GMV chênh lệch > 10%
            if (userGMV > 0 && Math.abs(userGMV - ocr.gmv) / ocr.gmv > 0.1) {
              warnings.push(`Doanh thu bạn nhập (${formatMoney(userGMV)}) chênh lệch >10% so với AI đọc (${formatMoney(ocr.gmv)})`);
            }
            // Cảnh báo platform không khớp
            if (ocr.platform !== 'unknown' && form.platform !== 'other' && form.platform !== ocr.platform) {
              warnings.push(`Bạn chọn ${form.platform} nhưng ảnh là ${ocr.platform.toUpperCase()}`);
            }
            if (warnings.length === 0) return null;
            return (
              <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2, fontSize: '0.8rem' }}>
                {warnings.map((w, i) => <div key={i}>{w}</div>)}
              </Alert>
            );
          })()}

          {/* OCR Info - Hiển thị thông tin AI đọc được */}
          {ocrResults[0] && ocrResults[0].gmv > 0 && (ocrResults[0].duration > 0 || ocrResults[0].orders > 0) && (
            <Box sx={{ mt: 1, p: 1, borderRadius: 2, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
              <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                AI đọc từ ảnh:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {ocrResults[0].platform !== 'unknown' && (
                  <Chip label={ocrResults[0].platform.toUpperCase()} size="small" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: ocrResults[0].platform === 'shopee' ? '#fee2e2' : '#e0e7ff', color: ocrResults[0].platform === 'shopee' ? '#dc2626' : '#4f46e5' }} />
                )}
                {ocrResults[0].duration > 0 && (
                  <Chip label={`${Math.floor(ocrResults[0].duration/60)}h${ocrResults[0].duration%60 > 0 ? ocrResults[0].duration%60 + 'p' : ''}`} size="small" sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#ecfdf5', color: '#059669' }} />
                )}
                {ocrResults[0].orders > 0 && (
                  <Chip label={`${ocrResults[0].orders} đơn`} size="small" sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#fef3c7', color: '#d97706' }} />
                )}
                {ocrResults[0].viewers > 0 && (
                  <Chip label={`${ocrResults[0].viewers.toLocaleString()} views`} size="small" sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#f3e8ff', color: '#7c3aed' }} />
                )}
                {ocrResults[0].date && (
                  <Chip label={ocrResults[0].date} size="small" sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#f1f5f9', color: '#475569' }} />
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>

        <Box sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            onClick={() => setDialogOpen(false)} variant="outlined" disabled={submitting}
            sx={{ borderRadius: 2, textTransform: 'none', px: 3, fontWeight: 600, borderColor: '#cbd5e1', color: '#64748b' }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={submitting}
            sx={{
              borderRadius: 2, textTransform: 'none', px: 3, fontWeight: 600,
              background: 'linear-gradient(135deg, #1e3a8a 0%, #059669 100%)',
              boxShadow: '0 4px 14px rgba(5,150,105,0.3)',
              '&:hover': { boxShadow: '0 6px 20px rgba(5,150,105,0.4)' }
            }}
          >
            {submitting ? 'Đang gửi...' : editingId ? 'Cập nhật & Gửi duyệt' : 'Gửi duyệt'}
          </Button>
        </Box>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, data: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MonetizationOnIcon color="primary" /> Chi tiết doanh thu
          {detailDialog.data?.isLocked && <LockIcon color="action" fontSize="small" />}
        </DialogTitle>
        {detailDialog.data && (
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Ngày</Typography>
                <Typography variant="body1" fontWeight={600}>{formatDate(detailDialog.data.date)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Nền tảng</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {detailDialog.data.platform === 'shopee' ? 'Shopee' : detailDialog.data.platform === 'tiktok' ? 'TikTok' : 'Khác'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Doanh thu</Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {formatMoney(detailDialog.data.totalSales)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Trạng thái</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    icon={statusConfig[detailDialog.data.status]?.icon}
                    label={statusConfig[detailDialog.data.status]?.label}
                    color={statusConfig[detailDialog.data.status]?.color}
                    size="small"
                  />
                </Box>
              </Grid>
              {detailDialog.data.sessionLink && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Link phiên live</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    <a href={detailDialog.data.sessionLink} target="_blank" rel="noopener noreferrer">
                      {detailDialog.data.sessionLink}
                    </a>
                  </Typography>
                </Grid>
              )}
              {detailDialog.data.sessionCode && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Mã phiên live</Typography>
                  <Typography variant="body2">{detailDialog.data.sessionCode}</Typography>
                </Grid>
              )}
              {detailDialog.data.notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Ghi chú</Typography>
                  <Typography variant="body2">{detailDialog.data.notes}</Typography>
                </Grid>
              )}
              {detailDialog.data.rejectionReason && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    <strong>Lý do từ chối:</strong> {detailDialog.data.rejectionReason}
                  </Alert>
                </Grid>
              )}
              {detailDialog.data.approvedBy && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Duyệt bởi: {detailDialog.data.approvedBy?.fullName} - {formatDateTime(detailDialog.data.approvedAt)}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {detailDialog.data.evidence?.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Ảnh bằng chứng</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {detailDialog.data.evidence.map((ev, idx) => (
                    <Box
                      key={ev._id || idx}
                      sx={{ width: 100, height: 100, borderRadius: 1, overflow: 'hidden', border: '1px solid #e2e8f0', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                      onClick={() => setImagePreview({ open: true, url: toDisplayableImageUrl(ev.url) })}
                    >
                      <img src={toDisplayableImageUrl(ev.url)} alt={`evidence-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </DialogContent>
        )}
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailDialog({ open: false, data: null })} variant="outlined" sx={{ borderRadius: 2 }}>
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={imagePreview.open} onClose={() => setImagePreview({ open: false, url: '' })} maxWidth="md">
        <DialogContent sx={{ p: 1 }}>
          <img src={imagePreview.url} alt="preview" style={{ maxWidth: '100%', maxHeight: '80vh' }} />
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditDialog.open} onClose={() => setAuditDialog({ open: false, logs: [] })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" /> Lịch sử thay đổi
        </DialogTitle>
        <DialogContent>
          {auditDialog.logs.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>Chưa có lịch sử</Typography>
          ) : (
            <Box>
              {auditDialog.logs.map((log, idx) => (
                <Card key={idx} variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Chip
                        label={
                          { created: 'Tạo mới', updated: 'Cập nhật', submitted: 'Gửi duyệt',
                            approved: 'Duyệt', rejected: 'Từ chối', locked: 'Khóa',
                            admin_edited: 'Admin sửa'
                          }[log.action] || log.action
                        }
                        size="small"
                        color={
                          { created: 'info', approved: 'success', rejected: 'error',
                            admin_edited: 'warning'
                          }[log.action] || 'default'
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(log.timestamp)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Bởi: <strong>{log.performedBy?.fullName || 'N/A'}</strong>
                    </Typography>
                    {log.reason && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Lý do: {log.reason}
                      </Typography>
                    )}
                    {log.changes?.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {log.changes.map((c, ci) => (
                          <Typography key={ci} variant="caption" display="block" color="text.secondary">
                            {c.field}: {c.oldValue != null ? String(c.oldValue) : '(trống)'} → {String(c.newValue)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAuditDialog({ open: false, logs: [] })} variant="outlined" sx={{ borderRadius: 2 }}>
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open} autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity} variant="filled" sx={{ borderRadius: 2 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RevenueSubmit;
